import { Component, OnInit, OnDestroy, signal, computed, output, Input, ChangeDetectorRef, inject } from '@angular/core';
import { FormControl, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OpenDialogService } from '@app/core/services/open-dialog.service';
import { HardwareVariantOfferingDetails } from '@app/sales/modules/accessories/models/hardware-offering-model';
import { HardwareSearchService } from '@app/sales/modules/accessories/services/hardware-search.service';
import { EquipmentEventService } from '@app/sales/modules/equipment/equipment-event.service';
import { IconNames } from '@app/shared/enums/icon-names';
import { debounceTime, distinctUntilChanged, finalize, Subscription, take, catchError, of } from 'rxjs';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { focusHtmlElementById, disableHtmlElementById, enableHtmlElementById } from '@app/core/utils';
import { EquipmentVariant } from '@app/sales/modules/equipment/equipment.models';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { select, Store } from '@ngxs/store';
import { CustomerProductMobilePhoneSelectors } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { IceCheckboxComponent } from '@app/shared/components/molecules/ice-checkbox/ice-checkbox.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { MatIcon } from '@angular/material/icon';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { OfferingService } from '@app/sales/modules/subscription/services/offering.service';
import { SetAvailableForChangeOfferingsAction } from '@app/sales/states/offering/offering.actions';

@Component({
  selector: 'app-equipment-identification',
  templateUrl: './equipment-identification.component.html',
  styleUrl: './equipment-identification.component.scss',
  imports: [
    IceInputFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    SpinnerComponent,
    IceAlertComponent,
    IceCheckboxComponent,
  ],
})
export class EquipmentIdentificationComponent implements OnInit, OnDestroy {
  openDialogService = inject(OpenDialogService);
  private formGroupDirective = inject(FormGroupDirective);
  private hardwareSearchService = inject(HardwareSearchService);
  private equipmentEventService = inject(EquipmentEventService);
  private offeringService = inject(OfferingService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private store = inject(Store);

  IconNames = IconNames;
  getGtinErrorText = FormValidationService.getGtinErrorText;
  getImeiErrorText = FormValidationService.getImeiErrorText;

  @Input({ required: true }) equipmentVariant: EquipmentVariant;

  isValidatingImei = select(CustomerProductMobilePhoneSelectors.isValidatingImei);
  isRetailPayment = select(DealerStateSelectors.isRetailPayment);
  private existingVoiceProducts = select(CustomerProductSelectors.getExistingVoiceProducts);
  private existingSubscriptionIds = computed(() => this.existingVoiceProducts().map(vp => vp.subscription?.id));

  fetchingProductData = output<boolean>();

  isFetchingProductData = signal(false);
  identifiedHardwareDetails = signal<HardwareVariantOfferingDetails>(undefined);
  showHeadsNotFoundAlert = signal(false);
  gtinElementId = 'gtinInput';
  imeiElementId = 'imeiInput';
  productNameElementId = 'productNameInput';
  missingIdentifiersElementId = 'missingIdentifiersInput';
  gtinDialogBtnId = 'gtin-dialog-btn';
  imeiDialogBtnId = 'imei-dialog-btn';

  get isMobilePhoneContext(): boolean {
    return this.equipmentVariant === EquipmentVariant.MobilePhone;
  }

  get gtinControl(): FormControl {
    return this.formGroupDirective.form?.controls.gtin as FormControl;
  }
  get imeiControl(): FormControl {
    return this.formGroupDirective.form?.controls.imei as FormControl;
  }
  get missingIdentifiersControl(): FormControl {
    return this.formGroupDirective.form?.controls.isMissingIdentifiers as FormControl;
  }
  get productNameControl(): FormControl {
    return this.formGroupDirective.form?.controls.productName as FormControl;
  }

  get isGtinValid(): boolean {
    return this.gtinControl?.valid;
  }
  get isImeiValid(): boolean {
    return this.gtinControl?.valid;
  }

  get shouldDisplayImeiInput(): boolean {
    return this.identifiedHardwareDetails()?.requireImei || this.isMobilePhoneContext || !!this.imeiControl?.value;
  }

  get shouldDisplayProductNameInput(): boolean {
    return (
      (this.identifiedHardwareDetails() === null && !this.missingIdentifiersControl?.value) ||
      !!this.productNameControl?.value
    );
  }

  get shouldDisplayMissingIdentifiersCheckbox(): boolean {
    return this.isMobilePhoneContext && this.shouldDisplayImeiInput && !!this.missingIdentifiersControl;
  }

  private subscriptions$: Subscription[] = [];

  ngOnInit(): void {
    this.fetchingProductData.subscribe(isFetching => this.isFetchingProductData.set(isFetching));
    if (this.missingIdentifiersControl) {
      this.updateEnabledIdentifierControls();
    }

    this.subscriptions$.push(
      this.gtinControl.valueChanges.pipe(distinctUntilChanged(), debounceTime(200)).subscribe(gtinValue => {
        this.showHeadsNotFoundAlert.set(false);

        if (this.gtinControl.status === 'VALID' && !this.isFetchingProductData()) {
          this.fetchingProductData.emit(true);
          this.checkHeadsProduct(gtinValue);
          this.fetchAvailableForChangeOfferings();
          this.hardwareSearchService
            .getHardwareWithGtin(gtinValue, this.existingSubscriptionIds())
            .pipe(
              take(1),
              finalize(() => this.fetchingProductData.emit(false))
            )
            .subscribe({
              next: response => {
                this.productNameControl?.disable();
                this.identifiedHardwareDetails.set(response);
                this.equipmentEventService.hardwareDetails.next(response);

                if (response?.requireImei && this.imeiControl) {
                  this.resetControl(this.productNameControl);
                  if (this.imeiControl.disabled) {
                    this.imeiControl.enable();
                  }
                  this.changeDetectorRef.detectChanges();
                  focusHtmlElementById(this.imeiElementId);
                } else {
                  this.resetControl(this.imeiControl);
                }
              },
              error: () => {
                this.productNameControl?.enable();

                if (this.isMobilePhoneContext && !this.missingIdentifiersControl?.value) {
                  this.imeiControl?.enable();
                } else if (!this.isMobilePhoneContext) {
                  this.resetControl(this.imeiControl);
                }

                this.setEmptyProductValues();
              },
            });
        }
      })
    );

    if (this.missingIdentifiersControl) {
      this.subscriptions$.push(
        this.missingIdentifiersControl.valueChanges.subscribe(() => {
          this.updateEnabledIdentifierControls();
        })
      );
    }

    if (this.productNameControl && !this.isMobilePhoneContext) {
      this.subscriptions$.push(
        this.productNameControl?.valueChanges.subscribe(value => {
          if (!value) return;
          this.equipmentEventService.hardwareDetails.next({
            ...this.identifiedHardwareDetails(),
            name: value,
          });
        })
      );
    }
  }

  private fetchAvailableForChangeOfferings(): void {
    this.offeringService
      .getAvailableForChangeOfferings(this.existingSubscriptionIds())
      .pipe(take(1))
      .subscribe({
        next: offerings => {
          if (!offerings) return;
          this.store.dispatch(new SetAvailableForChangeOfferingsAction(offerings));
        },
      });
  }

  private setEmptyProductValues(): void {
    this.identifiedHardwareDetails.set(null);
    this.equipmentEventService.hardwareDetails.next(null);
  }

  private resetControl(control: FormControl): void {
    if (control) {
      control.setValue('');
      control.disable();
      control.updateValueAndValidity();
    }
  }

  private updateEnabledIdentifierControls() {
    if (this.missingIdentifiersControl.value) {
      this.resetControl(this.gtinControl);
      this.resetControl(this.imeiControl);
      this.resetControl(this.productNameControl);

      disableHtmlElementById(this.gtinDialogBtnId);
      disableHtmlElementById(this.imeiDialogBtnId);

      this.setEmptyProductValues();
    } else {
      if (this.identifiedHardwareDetails() === null) {
        this.identifiedHardwareDetails.set(undefined);
      }

      this.gtinControl.enable();
      this.imeiControl.enable();
      enableHtmlElementById(this.gtinDialogBtnId);
      enableHtmlElementById(this.imeiDialogBtnId);
      this.productNameControl.enable();
    }
  }

  onGtinEnter($event: Event): void {
    $event.preventDefault();
    if (this.imeiControl) {
      focusHtmlElementById(this.imeiElementId);
    }
  }

  private checkHeadsProduct(gtin: string): void {
    if (!this.isRetailPayment()) {
      return;
    }

    this.hardwareSearchService
      .checkHeadsProductExists(gtin)
      .pipe(
        take(1),
        catchError(() => of(true)) // In case of error, assume the product exists to avoid showing the alert unnecessarily
      )
      .subscribe(exists => {
        this.showHeadsNotFoundAlert.set(!exists);
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(subscription => subscription.unsubscribe());
    this.equipmentEventService.hardwareDetails.next(undefined);
    this.store.dispatch(new SetAvailableForChangeOfferingsAction([]));
  }
}
